import {
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import React, { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Holder } from "../../interfaces";
import { citySelectors, holderActions, holderSelectors } from "../../redux";
import useHolderForm, { HolderFormType } from "./useHolderForm";

interface FormDialogHolderProps {
  holderToUpdate?: Holder;
  onClose: () => any;
  isOpen: boolean;
}

const FormDialogHolder: React.FC<FormDialogHolderProps> = ({
  holderToUpdate,
  onClose,
  isOpen,
}) => {
  const [alertOpen, setOpen] = React.useState(true);

  let holderInitialValue: HolderFormType | undefined = undefined;

  const { errors, register, handleSubmit, reset, control } = useHolderForm(
    holderInitialValue
  );
  const [hasBeenRequested, setHasBeenRequested] = useState(false);
  const dispatch = useDispatch();
  const cities = useSelector(citySelectors.getAllCities);
  const createdHolder = useSelector(holderSelectors.getCreatedHolder);

  useEffect(() => {
    if (!isOpen) {
      !holderInitialValue
        ? dispatch(holderActions.clear("createdHolder"))
        : dispatch(holderActions.clear("updatedHolder"));

      setHasBeenRequested(false);
      reset({});
    }
  }, [dispatch, holderInitialValue, isOpen, reset]);

  useEffect(() => {
    if (
      holderToUpdate &&
      holderToUpdate.email &&
      holderToUpdate.displayName &&
      holderToUpdate.phoneNumber
    ) {
      reset({
        cityId: holderToUpdate.cityId,
        email: holderToUpdate.email,
        name: holderToUpdate.displayName,
        phoneNumber: holderToUpdate.phoneNumber.replace("+39", ""),
        id: holderToUpdate.uid,
      });
    }
  }, [holderToUpdate, reset]);

  useEffect(() => {
    if (createdHolder.data) {
      reset({});
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  }, [createdHolder, onClose, reset]);

  const onSubmit = (data: HolderFormType) => {
    if (holderToUpdate) {
      dispatch(
        holderActions.updateHolder({
          uid: holderToUpdate.uid,
          cityId: data.cityId,
          displayName: data.name,
          phoneNumber: "+39" + data.phoneNumber,
          photoURL: "",
          email: data.email,
        })
      );
    } else
      dispatch(
        holderActions.createHolder({
          cityId: data.cityId,
          displayName: data.name,
          phoneNumber: "+39" + data.phoneNumber,
          photoURL: "",
          email: data.email,
        })
      );
    setHasBeenRequested(true);
    dispatch(holderActions.loadAllHolders({}));
  };

  const citiesOptions = useMemo(
    () =>
      cities.data?.results.map((city) => ({
        value: city.id,
        label: city.name,
      })),
    [cities.data]
  );

  return (
    <Dialog open={isOpen} onClose={onClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">
        Aggiungi un nuovo gestore
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <input name="id" ref={register} type="hidden"></input>
        <DialogContent>
          <DialogContentText>
            <Collapse
              component="div"
              in={!createdHolder.isLoading && hasBeenRequested && alertOpen}
            >
              <Alert
                color={createdHolder.error ? "error" : "success"}
                severity={createdHolder.error ? "error" : "success"}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {createdHolder.error
                  ? "Si è verificato un errore"
                  : "Dato aggiunto correttamente"}
              </Alert>
            </Collapse>
          </DialogContentText>

          <TextField
            label="Nome completo"
            helperText={errors.name?.message}
            error={!!errors.name}
            name="name"
            inputRef={register}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          <TextField
            id="outlined-full-width"
            label="Email"
            helperText={errors.email?.message}
            fullWidth
            error={!!errors.email}
            name="email"
            inputRef={register}
            type="email"
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          <TextField
            label="Numero di telefono"
            helperText={errors.phoneNumber?.message}
            inputRef={register}
            error={!!errors.phoneNumber}
            name="phoneNumber"
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          <Controller
            as={
              <TextField
                select
                margin="normal"
                label="Select"
                fullWidth
                helperText={errors.cityId?.message}
                error={!!errors.cityId}
                variant="outlined"
              >
                {citiesOptions?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            }
            name="cityId"
            defaultValue=""
            control={control}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Chiudi
          </Button>
          <Button type="submit" color="primary">
            Aggiungi
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default React.memo(FormDialogHolder);
